import type { FC, ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { BLOCKING_CONVERSATION_EVENT_TYPES_STRING } from '@/features/events/shared'
import { env } from '@/infra/config/env'
import { CLAUDE_CODE_CONVERSATION_EVENT } from '@/infra/sse'
import type { ConversationEventPayload } from '@/infra/sse/conversation/payloads'
import type { SoundProfile, SoundProfileName } from './audioProfiles'
import { SOUND_PROFILES, profileForEventType } from './audioProfiles'

type AudioContextState = 'closed' | 'suspended' | 'running'

interface AudioNotificationsContextValue {
  readonly isEnabled: boolean
  readonly isInitialized: boolean
  readonly isSupported: boolean
  readonly contextState: AudioContextState
  readonly enable: () => void
  readonly disable: () => void
  readonly toggle: () => void
  readonly playTestSound: () => Promise<void>
}

interface WebAudioContextResult {
  readonly isInitialized: boolean
  readonly contextState: AudioContextState
  readonly playSound: (profile: SoundProfileName) => Promise<void>
  readonly initialize: () => Promise<void>
}

interface AudioNotificationsProviderProps {
  readonly children: ReactNode
}

const STORAGE_KEY = 'audio-notifications-enabled'

const AudioNotificationsContext = createContext<AudioNotificationsContextValue | null>(null)

export const AudioNotificationsProvider: FC<AudioNotificationsProviderProps> = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState(getStoredEnabled)
  const isSupported = useMemo(getAudioCapabilities, [])
  const audio = useWebAudioContext(isSupported)
  const isEnabledRef = useRef(isEnabled)

  useEffect(() => {
    isEnabledRef.current = isEnabled
  }, [isEnabled])

  const enable = useCallback((): void => {
    setIsEnabled(true)
    setStoredEnabled(true)
  }, [])

  const disable = useCallback((): void => {
    setIsEnabled(false)
    setStoredEnabled(false)
  }, [])

  const toggle = useCallback((): void => {
    setIsEnabled((prev) => {
      const newValue = !prev
      setStoredEnabled(newValue)
      return newValue
    })
  }, [])

  const playTestSound = useCallback(async (): Promise<void> => {
    if (!audio.isInitialized) {
      await audio.initialize()
    }
    await audio.playSound('agentReply')
  }, [audio])

  useEffect(() => {
    const handleConversationEvent = (event: Event): void => {
      const customEvent = event as CustomEvent<ConversationEventPayload>
      const payload = customEvent.detail

      if (
        isEnabledRef.current &&
        payload?.eventType &&
        payload.source !== 'hydration' &&
        BLOCKING_CONVERSATION_EVENT_TYPES_STRING.has(payload.eventType)
      ) {
        void audio.playSound(profileForEventType(payload.eventType))
      }
    }

    globalThis.addEventListener(CLAUDE_CODE_CONVERSATION_EVENT, handleConversationEvent)
    return () =>
      globalThis.removeEventListener(CLAUDE_CODE_CONVERSATION_EVENT, handleConversationEvent)
  }, [audio])

  const value = useMemo<AudioNotificationsContextValue>(
    () => ({
      isEnabled,
      isInitialized: audio.isInitialized,
      isSupported,
      contextState: audio.contextState,
      enable,
      disable,
      toggle,
      playTestSound,
    }),
    [
      isEnabled,
      audio.isInitialized,
      isSupported,
      audio.contextState,
      enable,
      disable,
      toggle,
      playTestSound,
    ],
  )

  return (
    <AudioNotificationsContext.Provider value={value}>
      {children}
    </AudioNotificationsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAudioNotificationsContext(): AudioNotificationsContextValue {
  const context = useContext(AudioNotificationsContext)
  if (!context) {
    throw new Error('useAudioNotificationsContext must be used within AudioNotificationsProvider')
  }
  return context
}

function useWebAudioContext(isSupported: boolean): WebAudioContextResult {
  const audioContextRef = useRef<AudioContext | null>(null)
  const initializationAttemptedRef = useRef(false)
  const [isInitialized, setIsInitialized] = useState(false)
  const [contextState, setContextState] = useState<AudioContextState>('closed')

  const syncContextState = useCallback(() => {
    const state = audioContextRef.current?.state ?? 'closed'
    setContextState(state as AudioContextState)
  }, [])

  const initialize = useCallback(async (): Promise<void> => {
    if (initializationAttemptedRef.current || !isSupported) return

    const ctx = createAudioContext()
    if (!ctx) {
      setIsInitialized(false)
      return
    }

    audioContextRef.current = ctx
    initializationAttemptedRef.current = true
    setIsInitialized(true)

    if (ctx.state === 'suspended') {
      try {
        await ctx.resume()
      } catch {
        /* ignored */
      }
    }
    syncContextState()
  }, [isSupported, syncContextState])

  const playSound = useCallback(
    async (profileName: SoundProfileName): Promise<void> => {
      const ctx = audioContextRef.current
      if (!ctx || !isInitialized) return

      try {
        if (ctx.state === 'suspended') {
          await ctx.resume()
        }
        if (ctx.state !== 'running') return

        scheduleProfile(ctx, SOUND_PROFILES[profileName])
      } catch {
        /* ignored */
      }
    },
    [isInitialized],
  )

  useEffect(() => {
    if (!isSupported || initializationAttemptedRef.current) return

    const initOnInteraction = (): void => {
      void initialize()
      document.removeEventListener('click', initOnInteraction)
      document.removeEventListener('keydown', initOnInteraction)
    }

    document.addEventListener('click', initOnInteraction, { once: true })
    document.addEventListener('keydown', initOnInteraction, { once: true })

    return () => {
      document.removeEventListener('click', initOnInteraction)
      document.removeEventListener('keydown', initOnInteraction)
    }
  }, [isSupported, initialize])

  useEffect(() => {
    const interval = setInterval(syncContextState, env.NOTIFICATION_PERMISSION_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [syncContextState])

  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        void audioContextRef.current.close()
        audioContextRef.current = null
      }
    }
  }, [])

  return { isInitialized, contextState, playSound, initialize }
}

function getAudioCapabilities(): boolean {
  return (
    typeof globalThis !== 'undefined' &&
    ('AudioContext' in globalThis || 'webkitAudioContext' in globalThis)
  )
}

function getStoredEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === null ? true : stored === 'true'
}

function setStoredEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return
  localStorage.setItem(STORAGE_KEY, String(enabled))
}

function createAudioContext(): AudioContext | null {
  try {
    const AudioContextConstructor =
      globalThis.AudioContext ||
      (globalThis as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    return new AudioContextConstructor()
  } catch {
    return null
  }
}

function scheduleProfile(ctx: AudioContext, profile: SoundProfile): void {
  let cursor = ctx.currentTime
  for (const tone of profile.tones) {
    const start = cursor
    const end = start + tone.durationSeconds
    const fadeStart = Math.max(start, end - profile.fadeOutSeconds)

    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(tone.frequency, start)

    gainNode.gain.setValueAtTime(profile.volume, start)
    if (fadeStart > start) {
      gainNode.gain.setValueAtTime(profile.volume, fadeStart)
    }
    gainNode.gain.exponentialRampToValueAtTime(0.001, end)

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)
    oscillator.start(start)
    oscillator.stop(end)

    cursor = end + profile.gapSeconds
  }
}
